/**
 * CatalogPanel.jsx - Spare Parts > Catalog: every part, searchable by part #,
 * description, F-number, HTS code, ECCN, origin or anything in its notes
 * (the server's search, GET /api/spare-parts/parts?q=). Each part shows its
 * STANDARD (FANUC list) and GLOBAL price, the discount, and the day the price
 * was last checked as a chip: GREEN 0-3 months, YELLOW 4-8, RED 9+ ("Get
 * prices checked"), grey "No price yet". A banner counts the red and the
 * unpriced parts of the search; the chips filter by them. Click a row for the
 * part (PartDrawer, opened by the view).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import { listParts, errorOf } from './sparePartsApi';
import { BANDS, BAND_STYLE, fmtMoney, fmtPct, sortRows } from './sparePartsRules';
import { Fact, GREEN, LINE, MUTED, Msg, PriceChip, SortTh, num, td } from './spUi';
import { Btn, SelectInput, TextInput } from '../quotes/quoteUi';

const SHOW_MAX = 600;
const BAND_WORD = { green: 'Green', yellow: 'Yellow', red: 'Red', none: 'No price' };

export default function CatalogPanel({ editable, onOpenPart, onNewPart, tick = 0 }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState(''); // q, once typing pauses
  const [bands, setBands] = useState([]);
  const [inStock, setInStock] = useState(false);
  const [missingCustoms, setMissingCustoms] = useState(false);
  const [type, setType] = useState('');
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const reads = useRef(latestReads()).current;

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      const ticket = reads.start();
      const res = await listParts({ q: query, band: bands.join(','), inStock, missingCustoms, type });
      if (!res.ok) {
        if (!quiet && reads.show(ticket)) setMsg({ kind: 'error', text: errorOf(res, 'Could not load the parts.') });
        return;
      }
      if (!reads.show(ticket)) return;
      setData(res.body);
      setMsg(null);
    },
    [query, bands, inStock, missingCustoms, type, reads]
  );
  useEffect(() => {
    load();
  }, [load, tick]);
  useLiveRefresh('spare-parts', () => load({ quiet: true }));

  const rows = useMemo(() => sortRows(data ? data.rows : [], sort.key, sort.dir), [data, sort]);
  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const toggleBand = (b) => setBands((list) => (list.includes(b) ? list.filter((x) => x !== b) : [...list, b]));
  const counts = data ? data.counts : { green: 0, yellow: 0, red: 0, none: 0 };
  const total = data ? data.total : 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <TextInput
          aria-label="Search parts"
          value={q}
          onChange={setQ}
          placeholder="Search part #, F-number, description, HTS, ECCN, origin..."
          style={{ maxWidth: 380 }}
        />
        <SelectInput
          aria-label="Type"
          value={type}
          onChange={setType}
          style={{ width: 170 }}
          options={[
            { value: '', label: 'Parts and robots' },
            { value: 'part', label: 'Parts only' },
            { value: 'robot', label: 'Robot configurations' },
          ]}
        />
        <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 11.5, fontWeight: 600 }}>
          <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} /> In stock only
        </label>
        <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 11.5, fontWeight: 600 }}>
          <input type="checkbox" checked={missingCustoms} onChange={(e) => setMissingCustoms(e.target.checked)} /> Customs data missing
        </label>
        {editable && (
          <Btn primary small onClick={onNewPart} style={{ marginLeft: 'auto' }}>
            + Add part
          </Btn>
        )}
      </div>

      <div
        role="group"
        aria-label="Price age"
        style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 }}>Price checked</span>
        {BANDS.map((b) => {
          const on = bands.includes(b);
          const s = BAND_STYLE[b];
          return (
            <button
              key={b}
              type="button"
              aria-pressed={on}
              title={s.hint}
              onClick={() => toggleBand(b)}
              style={{
                padding: '3px 10px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'Montserrat',
                border: `1px solid ${on ? s.color : LINE}`,
                background: on ? s.bg : '#fff',
                color: on ? s.color : '#1F2328',
              }}
            >
              <span
                aria-hidden="true"
                style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 999, background: s.dot, marginRight: 5 }}
              />
              {BAND_WORD[b]} {counts[b]}
            </button>
          );
        })}
        {bands.length > 0 && (
          <Btn small onClick={() => setBands([])}>
            Clear
          </Btn>
        )}
      </div>

      {(counts.red > 0 || counts.none > 0) && (
        <div
          role="note"
          data-testid="stale-banner"
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '8px 12px',
            borderRadius: 8,
            marginBottom: 10,
            background: counts.red ? '#FFEBEE' : '#F1F3F4',
            color: counts.red ? '#B71C1C' : '#3C4043',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <span>
            {counts.red > 0 &&
              `${counts.red} part${counts.red === 1 ? '' : 's'} need${counts.red === 1 ? 's' : ''} prices checked (9+ months old)`}
            {counts.red > 0 && counts.none > 0 && ' · '}
            {counts.none > 0 && `${counts.none} part${counts.none === 1 ? ' has' : 's have'} no price yet`}
          </span>
          {counts.red > 0 && (
            <Btn small onClick={() => setBands(['red'])}>
              Show red
            </Btn>
          )}
          {counts.none > 0 && (
            <Btn small onClick={() => setBands(['none'])}>
              Show unpriced
            </Btn>
          )}
        </div>
      )}

      <Msg msg={msg} />
      {!data && !msg && <div style={{ padding: 30, color: MUTED, fontSize: 12 }}>Loading the parts...</div>}
      {data && (
        <>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 6 }}>
            {rows.length === total ? `${total} part${total === 1 ? '' : 's'}` : `${rows.length} of ${total} parts`}
            {query ? ` matching "${query}"` : ''}
            {rows.length > SHOW_MAX ? ` - showing the first ${SHOW_MAX}; search or filter to narrow` : ''}
          </div>
          <div
            style={{ overflow: 'auto', maxHeight: 'calc(100vh - 330px)', border: `1px solid ${LINE}`, borderRadius: 8, background: '#fff' }}
          >
            <table aria-label="Spare parts catalog" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <SortTh label="Part #" col="part_number" sort={sort} onSort={onSort} />
                  <SortTh label="Description" col="description" sort={sort} onSort={onSort} />
                  <SortTh label="Standard" col="standard_price" sort={sort} onSort={onSort} right title="FANUC list price" />
                  <SortTh label="GLOBAL" col="global_price" sort={sort} onSort={onSort} right title="What GLOBAL pays" />
                  <SortTh label="Discount" col="discount_pct" sort={sort} onSort={onSort} right title="1 - GLOBAL / Standard" />
                  <SortTh label="Price checked" col="price_checked_at" sort={sort} onSort={onSort} />
                  <SortTh label="On hand" col="on_hand" sort={sort} onSort={onSort} right />
                  <SortTh label="Origin" col="country_of_origin" sort={sort} onSort={onSort} />
                  <SortTh label="ECCN" col="eccn" sort={sort} onSort={onSort} />
                  <SortTh label="HTSUS" col="htsus" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, SHOW_MAX).map((p) => (
                  <tr key={p.id} onClick={() => onOpenPart(p.id)} style={{ cursor: 'pointer' }} data-testid="part-row">
                    <td style={{ ...td, fontWeight: 700, color: GREEN, whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenPart(p.id);
                        }}
                        style={{ all: 'unset', cursor: 'pointer' }}
                      >
                        {p.part_number}
                      </button>
                      {p.part_type === 'robot' && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 9.5,
                            fontWeight: 700,
                            color: '#1565C0',
                            background: '#E3F2FD',
                            padding: '1px 6px',
                            borderRadius: 4,
                          }}
                        >
                          ROBOT
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, maxWidth: 320 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.description || ''}>
                        {p.description || '-'}
                      </div>
                      {p.f_numbers && <div style={{ fontSize: 10.5, color: MUTED }}>On {p.f_numbers}</div>}
                    </td>
                    <td style={num}>{fmtMoney(p.standard_price, p.currency)}</td>
                    <td style={{ ...num, fontWeight: 700 }}>{fmtMoney(p.global_price, p.currency)}</td>
                    <td style={num}>{fmtPct(p.discount_pct)}</td>
                    <td style={td}>
                      <PriceChip age={p.price_age} checkedAt={p.price_checked_at} compact />
                    </td>
                    <td style={{ ...num, fontWeight: p.on_hand ? 700 : 400, color: p.on_hand ? GREEN : MUTED }}>{p.on_hand || 0}</td>
                    <td style={td}>{p.country_of_origin || <Missing />}</td>
                    <td style={td}>{p.eccn || <Missing />}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.htsus || <Missing />}</td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={10} style={{ ...td, textAlign: 'center', color: MUTED, padding: 24 }}>
                      No parts match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
            <Fact label="Colour rule">GREEN checked 0-3 months ago · YELLOW 4-8 · RED 9+ (FANUC changes prices once or twice a year)</Fact>
          </div>
        </>
      )}
      <style>{'table[aria-label="Spare parts catalog"] tbody tr:hover td { background: #F6F9F2; }'}</style>
    </div>
  );
}

function Missing() {
  return (
    <span title="Needed for the shipping documents" style={{ color: '#B07503', fontWeight: 700 }}>
      missing
    </span>
  );
}
